import React from 'react';
import { Label } from 'reactstrap';
import classnames from 'classnames';
import FilterableSelect from '../../../../../../common/components/FilterableSelect/FilterableSelect';
import {
  generateBasicStructure,
  nameById,
} from '../../../../../../common/components/FilterableSelect/filterable-select-helpers';
import { ErrorTooltip } from '../../../../../../common/components/ErrorTooltip/ErrorTooltip';
import TextInput from '../../../../../../common/components/TextInput';
import SelectorWithArrow from '../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import {
  AdSetBudgetType,
  CampaignBudgetType,
} from '@mm/state/interfaces/adAccounts';
import Hint from '../../../../../../common/components/Hint/Hint';
import Datetime from '../../../../../../common/components/DateTimePicker/DateTimePicker';
import { Targeting } from '../Targeting/Targeting';
import { filteredAudiences } from '../Targeting/utils';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import AdConfigModel, {
  MMTargeting,
  OptimizationGoal,
} from '@mm/state/model/AdConfigModel';
import { AdSetWithRelationsModel } from '@mm/state/model/AdSetModel';
import { numericValue } from '../../../utils/utils';
import { StoreState } from '@mm/state/store-interfaces';
import { useSelector } from 'react-redux';
import { CustomAudience } from '@mm/state/model/AdAccountModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { InfoCircleFilled } from '@ant-design/icons';

const renderAdSetHints = (
  specialCategorySelected: boolean,
  customAudiencesMismatching: string[],
) => (
  <>
    {specialCategorySelected && (
      <div className="ad-detail__subtext">
        <InfoCircleFilled className="ad-detail__info-circle" />
        Targeting will be limited since your campaign is labeled with a Special
        Ad Category
      </div>
    )}
    {customAudiencesMismatching.length > 0 && (
      <div className="ad-detail__subtext">
        <InfoCircleFilled className="ad-detail__info-circle" />
        The custom audiences used in this ad set need to be shared with the ad
        account you selected above. Please share following audiences with the
        account if want to use it with the selected ad set:{' '}
        {customAudiencesMismatching.join('; ')}
      </div>
    )}
  </>
);

const optimizationGoals: { name: string; id: OptimizationGoal }[] = [
  {
    name: 'Optimize on CONVERSATIONS',
    id: 'conversations',
  },
  {
    name: 'Optimize on LINK CLICKS',
    id: 'link_clicks',
  },
];

const adSetCloning = (adConfig: AdConfigModel | null) =>
  !adConfig || adConfig.targeting === null;

type Props = {
  // utility values
  adConfig: AdConfigModel | null;
  specialAdCategoryChecked: boolean;
  showAdSetsSpinner: boolean;
  budgetOptimizationOn: boolean;
  currency: string;
  scheduledContinuously: boolean;
  // fields
  adSetId: string;
  adSetName: string;
  adSetBudgetType: AdSetBudgetType | null;
  campaignBudgetType: CampaignBudgetType | null;
  adSetBudget: string | number | null;
  bidAmount: string | number | null;
  optimizationGoal: OptimizationGoal | null;
  adSetStartDate: Date;
  adSetEndDate: Date;
  targeting: MMTargeting;
  // collections
  customAudiences: CustomAudience[];
  adSets: AdSetWithRelationsModel[];
  // callbacks
  onChangeAdSetName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlurAdSetName: () => void;
  onChangeAdSetBudget: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlurAdSetBudget: () => void;
  onChangeAdSetBudgetType: (id: AdSetBudgetType) => void;
  onSelectCloneAdSet: () => void;
  onSelectNewAdSet: () => void;
  onAdSetChange: (id: number) => void;
  onChangeBidAmount: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlurBidAmount: () => void;
  onChangeEndDate: (date: Date) => void;
  onBlurEndDate: () => void;
  onChangeStartDate: (date: Date) => void;
  onBlurStartDate: () => void;
  onChangeTargeting: (targeting: MMTargeting) => void;
  onScheduleContinuously: () => void;
  onChangeOptimizationGoal: (id: OptimizationGoal) => void;
  onScheduleRanged: () => void;
  // validations
  customAudiencesMismatching: string[];
  adSetValidations?: string[];
  adSetNameValidations?: string[];
  adSetBudgetValidations?: string[];
  bidAmountValidations?: string[];

  usedInSlideout?: boolean;
};

const adSetBudgets = [
  { id: AdSetBudgetType.DAILY, name: 'Daily' },
  { id: AdSetBudgetType.LIFETIME, name: 'Lifetime' },
];

export const AdSetStep = (props: Props) => {
  const {
    adConfig,
    currency,
    specialAdCategoryChecked,
    customAudiencesMismatching,
    onSelectCloneAdSet,
    onSelectNewAdSet,
    onAdSetChange,
    showAdSetsSpinner,
    budgetOptimizationOn,
    adSetId,
    adSetName,
    adSetBudgetType,
    adSetBudget,
    bidAmount,
    optimizationGoal,
    campaignBudgetType,
    adSetValidations,
    adSetNameValidations,
    adSetBudgetValidations,
    bidAmountValidations,
    onChangeAdSetName,
    onBlurAdSetName,
    onChangeAdSetBudget,
    onBlurAdSetBudget,
    onChangeAdSetBudgetType,
    onBlurBidAmount,
    onChangeBidAmount,
    onChangeStartDate,
    onChangeEndDate,
    onChangeOptimizationGoal,
    onBlurStartDate,
    onBlurEndDate,
    onScheduleRanged,
    onScheduleContinuously,
    adSetStartDate,
    adSetEndDate,
    targeting,
    customAudiences,
    onChangeTargeting,
    scheduledContinuously,
    adSets,
    usedInSlideout,
  } = props;

  const page = useSelector(activeFacebookPageSelector);
  const defaultGoal: OptimizationGoal = page?.euLimitsAt
    ? 'link_clicks'
    : 'conversations';
  const defaultGoalName = optimizationGoals.find(
    g => g.id === defaultGoal,
  )?.name;
  const draft = adConfig?.draft;
  const inProgress = adConfig?.publishingProcess;
  const disabled = !adConfig || !draft || inProgress;
  const cloneAdSetSelected = adSetCloning(adConfig);
  const radioLabelClasses = classnames('ad-detail__radio-label', {
    'ad-detail__radio-label--disabled': disabled,
  });
  const mmAudiences = useSelector(
    (state: StoreState) => state.audiences.entities,
  );

  return (
    <>
      {renderAdSetHints(specialAdCategoryChecked, customAudiencesMismatching)}
      <div className="ad-config-detail__ad-set-selection ad-detail__radio-row">
        <div className="ad-config-detail__clone-ad-set ad-detail__radio-container">
          <input
            type="radio"
            id="clone-ad-set-checker"
            className="ad-detail__radio-input"
            disabled={disabled}
            checked={cloneAdSetSelected}
            onChange={onSelectCloneAdSet}
          />
          <Label className={radioLabelClasses} for="clone-ad-set-checker">
            Clone existing
          </Label>
        </div>
        <div className="ad-config-detail__new-ad-set ad-detail__radio-container">
          <input
            type="radio"
            id="new-ad-set-checker"
            className="ad-detail__radio-input"
            disabled={disabled}
            checked={!cloneAdSetSelected}
            onChange={onSelectNewAdSet}
          />
          <Label className={radioLabelClasses} for="new-ad-set-checker">
            Create new
          </Label>
        </div>
      </div>
      {cloneAdSetSelected && (
        <div className="ad-detail__inputs-container">
          <div
            className={classnames('ad-detail__input-container', {
              'ad-detail__input-container-slideout': usedInSlideout,
            })}
          >
            <div className="ad-detail__field-title">Select Ad Set to clone</div>
            <div className="ad-config-detail__ad-set-selector ad-detail__selector">
              <FilterableSelect
                className="ad-config-detail__highlighted-dropdown"
                showSpinner={showAdSetsSpinner}
                dynamicChangeSuggestions={true}
                onChange={onAdSetChange}
                outerValue={nameById(
                  adSetId,
                  adSets.map(adSet => ({
                    ...adSet,
                    id: Number(adSet.id),
                  })),
                  adConfig?.sourceAdSetId
                    ? `Ad set ${adConfig.sourceAdSetId} (Access restricted)`
                    : 'No Source Ad Set',
                )}
                suggestions={generateBasicStructure(adSets)}
                placeholder="Select Ad Set"
                disabled={disabled}
              />
              <ErrorTooltip
                classPrefix="ad-detail"
                validation={adSetValidations}
                hoverMode
              />
            </div>
          </div>
        </div>
      )}
      <div className="ad-detail__inputs-container">
        <div
          className={classnames('ad-detail__input-container', {
            'ad-detail__input-container-slideout': usedInSlideout,
          })}
        >
          <div className="ad-detail__field-title">New Ad Set Name</div>
          <div className="ad-detail__text-input-container">
            <TextInput
              className="ad-detail__input"
              placeholder="Enter ad set name"
              value={adSetName}
              onChange={onChangeAdSetName}
              onBlur={onBlurAdSetName}
              disabled={inProgress}
            />
            <ErrorTooltip
              classPrefix="ad-detail"
              validation={adSetNameValidations}
              hoverMode
            />
          </div>
        </div>
        {!budgetOptimizationOn && (
          <div
            className={classnames('ad-config-detail__budget-config', {
              'ad-config-detail__budget-config-slideout': usedInSlideout,
            })}
          >
            <div className="ad-config-detail__budget-selectors">
              <div className="ad-config-detail__budget-selector">
                <div className="ad-detail__field-title">Ad Set Budget</div>
                <div className="ad-config-detail__budget-dropdown ad-detail__selector">
                  <SelectorWithArrow
                    selectedItem={adSetBudgets.find(
                      item => item.id === adSetBudgetType,
                    )}
                    onChange={onChangeAdSetBudgetType}
                    disabled={disabled}
                    items={adSetBudgets}
                  />
                </div>
              </div>
              <div className="ad-config-detail__budget-selector">
                <div className="ad-detail__text-input-container">
                  <TextInput
                    className="ad-detail__input"
                    placeholder="Enter budget"
                    value={numericValue(adSetBudget)}
                    onChange={onChangeAdSetBudget}
                    type="number"
                    disabled={disabled}
                    onBlur={onBlurAdSetBudget}
                  />
                  <ErrorTooltip
                    classPrefix="ad-detail"
                    validation={adSetBudgetValidations}
                    hoverMode
                  />
                </div>
              </div>
            </div>
            <div className="ad-detail__subtext">
              <InfoCircleFilled className="ad-detail__info-circle" />
              Budget will be set to the currency used in your ad account (
              {currency})
            </div>
          </div>
        )}
      </div>
      <div className="ad-detail__inputs-container">
        <div
          className={classnames('ad-detail__input-container', {
            'ad-detail__input-container-slideout': usedInSlideout,
          })}
        >
          <div className="ad-detail__field-title">Maximum Bid</div>
          <div className="ad-detail__text-input-container">
            <TextInput
              className="ad-detail__input"
              placeholder="Enter bid amount"
              value={numericValue(bidAmount)}
              type="number"
              disabled={disabled}
              onChange={onChangeBidAmount}
              onBlur={onBlurBidAmount}
            />
            <ErrorTooltip
              classPrefix="ad-detail"
              validation={bidAmountValidations}
              hoverMode
            />
          </div>
          <div className="ad-detail__subtext">
            <InfoCircleFilled className="ad-detail__info-circle" />
            <div>
              Bid will be set to the currency used in your ad account (
              {currency})
            </div>
          </div>
        </div>
      </div>
      <hr className="ad-detail__separator" />
      <div className="ad-detail__inputs-container">
        <div
          className={classnames('ad-detail__input-container', {
            'ad-detail__input-container-slideout': usedInSlideout,
          })}
        >
          <div className="ad-detail__field-title">Optimization Goal</div>
          <div className="ad-config-detail__ad-account-selector ad-detail__selector">
            <FilterableSelect
              onChange={onChangeOptimizationGoal}
              outerValue={
                optimizationGoals.find(goal => goal.id === optimizationGoal)
                  ?.name || defaultGoalName
              }
              suggestions={generateBasicStructure(optimizationGoals)}
              placeholder="Select Optimization Goal"
              disabled={disabled}
            />
          </div>
          {page?.euLimitsAt && (
            <div className="ad-detail__subtext">
              <InfoCircleFilled className="ad-detail__info-circle" />
              <div>
                Ad Accounts subject to EU policy restrictions are not allowed to
                optimize on CONVERSATIONS
              </div>
            </div>
          )}
        </div>
      </div>
      <hr className="ad-detail__separator" />
      <div>
        <div className="ad-detail__field-title">Schedule</div>
        <div className="ad-config-detail__schedule-choices">
          <div className="ad-config-detail__schedule-continuously ad-detail__radio-container">
            <input
              type="radio"
              className="ad-detail__radio-input"
              id="schedule-from-today-checker"
              disabled={
                disabled ||
                adSetBudgetType === AdSetBudgetType.LIFETIME ||
                campaignBudgetType === CampaignBudgetType.LIFETIME
              }
              checked={scheduledContinuously}
              onChange={onScheduleContinuously}
            />
            <Label
              className={radioLabelClasses}
              for="schedule-from-today-checker"
            >
              Run continuously starting today
            </Label>
          </div>
          <div className="ad-config-detail__schedule-range ad-detail__radio-container">
            <input
              type="radio"
              className="ad-detail__radio-input"
              id="schedule-range-checker"
              disabled={!draft}
              checked={!scheduledContinuously}
              onChange={onScheduleRanged}
            />
            <Label className={radioLabelClasses} for="schedule-range-checker">
              Set Start & End Date
            </Label>
          </div>
        </div>
        {!scheduledContinuously && (
          <div className="ad-config-detail__scheduling-container">
            <div className="ad-config-detail__scheduling-dates">
              <div className="ad-detail__inputs-container">
                <div className="ad-detail__input-container ad-detail__full-input-container">
                  <div className="ad-detail__field-title">Start</div>
                  <div className="ad-config-detail__date-selector">
                    <Datetime
                      disabled={disabled}
                      value={adSetStartDate}
                      onChange={onChangeStartDate}
                      onClose={onBlurStartDate}
                    />
                  </div>
                </div>
              </div>
              <div className="ad-config-detail__targeting-age-separator">—</div>
              <div className="ad-detail__inputs-container">
                <div className="ad-detail__input-container ad-detail__full-input-container">
                  <div className="ad-detail__field-title">End</div>
                  <div className="ad-config-detail__date-selector">
                    <Datetime
                      disabled={disabled}
                      value={adSetEndDate}
                      onChange={onChangeEndDate}
                      onClose={onBlurEndDate}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {!cloneAdSetSelected && (
        <>
          <hr className="ad-detail__separator" />
          <Targeting
            specialAdCategory={specialAdCategoryChecked}
            disabled={Boolean(!draft || inProgress)}
            searchEntity={{ type: 'ctm_ad', id: adConfig?.id }}
            targeting={targeting}
            remoteAudiences={filteredAudiences(
              customAudiences,
              specialAdCategoryChecked,
            )}
            mmAudiences={(mmAudiences || []).filter(
              (aud: AudiencesModel) =>
                !aud.messengerAdAudience && !aud.adAudience,
            )}
            onChangeTargeting={onChangeTargeting}
            usedInSlideout={usedInSlideout}
          />
        </>
      )}
    </>
  );
};
